import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { resourcesAPI, API_BASE_URL, trackActivity } from '../services/api';
import { usePreview } from '../contexts/PreviewContext';
import PDFPreviewModal from '../components/PDFPreviewModal';
import ModalShell from '../components/ModalShell';

// File type configuration with colors and icons
const fileTypeConfig = {
  pdf: { color: '#E53935', bgColor: 'rgba(229, 57, 53, 0.1)', label: 'PDF', icon: 'pdf' },
  doc: { color: '#1976D2', bgColor: 'rgba(25, 118, 210, 0.1)', label: 'DOC', icon: 'doc' },
  docx: { color: '#1976D2', bgColor: 'rgba(25, 118, 210, 0.1)', label: 'DOCX', icon: 'doc' },
  xls: { color: '#388E3C', bgColor: 'rgba(56, 142, 60, 0.1)', label: 'XLS', icon: 'excel' },
  xlsx: { color: '#388E3C', bgColor: 'rgba(56, 142, 60, 0.1)', label: 'XLSX', icon: 'excel' },
  csv: { color: '#388E3C', bgColor: 'rgba(56, 142, 60, 0.1)', label: 'CSV', icon: 'excel' },
  ppt: { color: '#F57C00', bgColor: 'rgba(245, 124, 0, 0.1)', label: 'PPT', icon: 'ppt' },
  pptx: { color: '#F57C00', bgColor: 'rgba(245, 124, 0, 0.1)', label: 'PPTX', icon: 'ppt' },
  jpg: { color: '#7B1FA2', bgColor: 'rgba(123, 31, 162, 0.1)', label: 'JPG', icon: 'image' },
  jpeg: { color: '#7B1FA2', bgColor: 'rgba(123, 31, 162, 0.1)', label: 'JPEG', icon: 'image' },
  png: { color: '#7B1FA2', bgColor: 'rgba(123, 31, 162, 0.1)', label: 'PNG', icon: 'image' },
  gif: { color: '#7B1FA2', bgColor: 'rgba(123, 31, 162, 0.1)', label: 'GIF', icon: 'image' },
  mp4: { color: '#C2185B', bgColor: 'rgba(194, 24, 91, 0.1)', label: 'MP4', icon: 'video' },
  mov: { color: '#C2185B', bgColor: 'rgba(194, 24, 91, 0.1)', label: 'MOV', icon: 'video' },
  avi: { color: '#C2185B', bgColor: 'rgba(194, 24, 91, 0.1)', label: 'AVI', icon: 'video' },
  mp3: { color: '#00897B', bgColor: 'rgba(0, 137, 123, 0.1)', label: 'MP3', icon: 'audio' },
  wav: { color: '#00897B', bgColor: 'rgba(0, 137, 123, 0.1)', label: 'WAV', icon: 'audio' },
  zip: { color: '#5D4037', bgColor: 'rgba(93, 64, 55, 0.1)', label: 'ZIP', icon: 'archive' },
  link: { color: '#0288D1', bgColor: 'rgba(2, 136, 209, 0.1)', label: 'LINK', icon: 'link' },
  default: { color: 'var(--primary-color)', bgColor: 'rgba(0, 51, 102, 0.1)', label: 'FILE', icon: 'file' },
};

const getFileTypeInfo = (item) => {
  if (item.item_type === 'link') return fileTypeConfig.link;
  const ext = item.file_name?.split('.').pop()?.toLowerCase();
  return fileTypeConfig[ext] || fileTypeConfig.default;
};

// Icon components
const FileIcon = ({ type, size = 24 }) => {
  const icons = {
    pdf: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <path d="M9 13h6" />
        <path d="M9 17h6" />
      </svg>
    ),
    doc: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <line x1="10" y1="9" x2="8" y2="9" />
      </svg>
    ),
    excel: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <rect x="8" y="12" width="8" height="6" rx="1" />
      </svg>
    ),
    ppt: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <rect x="8" y="12" width="8" height="5" rx="1" />
      </svg>
    ),
    image: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21 15 16 10 5 21" />
      </svg>
    ),
    video: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="2" y="4" width="20" height="16" rx="2" />
        <polygon points="10 8 16 12 10 16 10 8" fill="currentColor" />
      </svg>
    ),
    audio: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M9 18V5l12-2v13" />
        <circle cx="6" cy="18" r="3" />
        <circle cx="18" cy="16" r="3" />
      </svg>
    ),
    archive: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <polyline points="21 8 21 21 3 21 3 8" />
        <rect x="1" y="3" width="22" height="5" />
        <line x1="10" y1="12" x2="14" y2="12" />
      </svg>
    ),
    link: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
        <polyline points="15 3 21 3 21 9" />
        <line x1="10" y1="14" x2="21" y2="3" />
      </svg>
    ),
    file: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
      </svg>
    ),
    folder: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
      </svg>
    ),
    folderOpen: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
        <path d="M2 10h20" />
      </svg>
    ),
  };
  return icons[type] || icons.file;
};

// Category icon map
const categoryIconMap = {
  'file-text': (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  ),
  folder: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
    </svg>
  ),
  archive: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <polyline points="21 8 21 21 3 21 3 8" />
      <rect x="1" y="3" width="22" height="5" />
      <line x1="10" y1="12" x2="14" y2="12" />
    </svg>
  ),
  book: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  ),
  briefcase: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
  ),
  building: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
      <line x1="9" y1="6" x2="9" y2="6.01" />
      <line x1="15" y1="6" x2="15" y2="6.01" />
      <line x1="9" y1="10" x2="9" y2="10.01" />
      <line x1="15" y1="10" x2="15" y2="10.01" />
      <line x1="9" y1="14" x2="9" y2="14.01" />
      <line x1="15" y1="14" x2="15" y2="14.01" />
      <path d="M9 22v-4h6v4" />
    </svg>
  ),
  calendar: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  'bar-chart': (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <line x1="12" y1="20" x2="12" y2="10" />
      <line x1="18" y1="20" x2="18" y2="4" />
      <line x1="6" y1="20" x2="6" y2="16" />
    </svg>
  ),
  'pie-chart': (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
      <path d="M22 12A10 10 0 0 0 12 2v10z" />
    </svg>
  ),
  'trending-up': (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
      <polyline points="17 6 23 6 23 12" />
    </svg>
  ),
  clipboard: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
    </svg>
  ),
  'dollar-sign': (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  ),
  globe: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  ),
  layers: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <polygon points="12 2 2 7 12 12 22 7 12 2" />
      <polyline points="2 17 12 22 22 17" />
      <polyline points="2 12 12 17 22 12" />
    </svg>
  ),
  lock: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  ),
  mail: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  ),
  search: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  ),
  shield: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  star: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  ),
  users: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  video: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <polygon points="10 8 16 12 10 16 10 8" />
    </svg>
  ),
  image: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </svg>
  ),
  link: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  ),
  settings: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  ),
};

const getCategoryIcon = (iconName) => categoryIconMap[iconName] || categoryIconMap['file-text'];

const ResourcesApp = () => {
  const { previewDomain, isPreviewMode } = usePreview();
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryData, setCategoryData] = useState({ root_items: [], folders: [] });
  const [loadingItems, setLoadingItems] = useState(false);
  const [expandedFolders, setExpandedFolders] = useState({});
  const VALID_VIEW_MODES = ['grid', 'list', 'table', 'compact', 'timeline'];
  const [viewMode, setViewMode] = useState(() => {
    const stored = localStorage.getItem('dcp_resources_viewMode');
    return VALID_VIEW_MODES.includes(stored) ? stored : 'grid';
  });

  // Persist view mode preference
  useEffect(() => {
    localStorage.setItem('dcp_resources_viewMode', viewMode);
  }, [viewMode]);

  // Table view sort state
  const [tableSortColumn, setTableSortColumn] = useState('title');
  const [tableSortDirection, setTableSortDirection] = useState('asc');
  const [searchQuery, setSearchQuery] = useState('');
  const [downloadingFolder, setDownloadingFolder] = useState(null);
  const [openPdfModals, setOpenPdfModals] = useState([]);
  const [openVideoModals, setOpenVideoModals] = useState([]);
  const modalIdRef = useRef(0);
  const nextModalId = () => ++modalIdRef.current;
  const [highlightedItemId, setHighlightedItemId] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimerRef = useRef(null);
  const deepLinkHandled = useRef(false);

  // Debounced global search
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

    if (!searchQuery || searchQuery.trim().length < 2) {
      setSearchResults(null);
      return;
    }

    setSearchLoading(true);
    searchTimerRef.current = setTimeout(async () => {
      try {
        const results = await resourcesAPI.searchItems(searchQuery.trim());
        setSearchResults(results);
      } catch (err) {
        console.error('Search error:', err);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, [searchQuery]);

  useEffect(() => {
    trackActivity('app_access', 'resources');
    fetchCategories();
  }, [isPreviewMode, previewDomain]);

  // Auto-select category: deep-link param or first category
  useEffect(() => {
    if (categories.length > 0 && !selectedCategory) {
      const categoryParam = searchParams.get('category');
      if (categoryParam && !deepLinkHandled.current) {
        const target = categories.find(c => String(c.id) === categoryParam);
        if (target) {
          handleCategoryClick(target);
          return;
        }
      }
      handleCategoryClick(categories[0]);
    }
  }, [categories]);

  // Handle deep-link folder expansion and item highlight after category data loads
  useEffect(() => {
    if (deepLinkHandled.current || loadingItems || !selectedCategory) return;
    const categoryParam = searchParams.get('category');
    const folderParam = searchParams.get('folder');
    const itemParam = searchParams.get('item');
    if (!categoryParam) return;

    // Only process once per navigation
    deepLinkHandled.current = true;

    if (folderParam) {
      // Ensure the target folder (and parents) are expanded
      setExpandedFolders(prev => {
        const next = { ...prev };
        // Expand the target folder
        next[parseInt(folderParam)] = true;
        // Also walk parent folders if nested
        const findParent = (folders, targetId) => {
          for (const f of folders) {
            if (f.id === targetId) return true;
            if (f.subfolders?.length) {
              if (findParent(f.subfolders, targetId)) {
                next[f.id] = true;
                return true;
              }
            }
          }
          return false;
        };
        if (categoryData.folders) {
          findParent(categoryData.folders, parseInt(folderParam));
        }
        return next;
      });
    }

    if (itemParam) {
      setHighlightedItemId(parseInt(itemParam));
      // Scroll to and clear highlight after animation
      setTimeout(() => {
        const el = document.querySelector(`[data-resource-id="${itemParam}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);
      setTimeout(() => setHighlightedItemId(null), 3000);
    }

    // Clean up URL params without triggering a re-render loop
    setSearchParams({}, { replace: true });
  }, [categoryData, loadingItems]);

  const fetchCategories = async () => {
    setIsLoading(true);
    try {
      let data;
      if (isPreviewMode && previewDomain) {
        // Fetch preview data
        const previewResponse = await resourcesAPI.previewAsClient(previewDomain);
        setCategories(previewResponse.categories || []);
      } else {
        // Normal fetch
        data = await resourcesAPI.getCategories();
        setCategories(data);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCategoryClick = async (category) => {
    setSelectedCategory(category);
    setLoadingItems(true);
    setExpandedFolders({});
    setSearchQuery('');
    try {
      const data = await resourcesAPI.getCategoryItems(category.id);
      setCategoryData(data);
      // Start with all folders collapsed
      setExpandedFolders({});
    } catch (err) {
      console.error('Error fetching items:', err);
    } finally {
      setLoadingItems(false);
    }
  };

  const toggleFolder = (folderId) => {
    setExpandedFolders(prev => ({
      ...prev,
      [folderId]: !prev[folderId]
    }));
  };

  // Handle preview (opens modal for PDFs/videos)
  const handlePreview = (item) => {
    const ext = item.file_name?.split('.').pop()?.toLowerCase();

    if (ext === 'pdf') {
      const pdfUrl = `${API_BASE_URL}/api/resources/preview/${item.id}`;
      setOpenPdfModals(prev => [...prev, { id: nextModalId(), url: pdfUrl, title: item.title || item.file_name }]);
    } else {
      const videoExtensions = ['mp4', 'webm', 'mov', 'avi', 'mkv'];
      if (videoExtensions.includes(ext)) {
        const videoUrl = `${API_BASE_URL}/api/resources/download/${item.id}`;
        setOpenVideoModals(prev => [...prev, { id: nextModalId(), url: videoUrl, title: item.title || item.file_name }]);
      }
    }
  };

  // Handle actual download (opens in new window/tab)
  const handleActualDownload = (item) => {
    const token = localStorage.getItem('access_token');
    const downloadUrl = `${API_BASE_URL}/api/resources/download/${item.id}?token=${token}`;
    window.open(downloadUrl, '_blank');
  };

  // Main click handler - shows preview for PDFs/videos, downloads others
  const handleDownload = async (item) => {
    if (item.item_type === 'link') {
      window.open(item.external_url, '_blank');
      return;
    }

    const ext = item.file_name?.split('.').pop()?.toLowerCase();
    const isPDF = ext === 'pdf';
    const isVideo = ['mp4', 'webm', 'mov', 'avi', 'mkv'].includes(ext);

    // PDFs and videos show preview by default
    if (isPDF || isVideo) {
      handlePreview(item);
    } else {
      // Other files download in new window
      handleActualDownload(item);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Sort items for table view
  const getSortedItems = useCallback((items) => {
    return [...items].sort((a, b) => {
      let cmp = 0;
      switch (tableSortColumn) {
        case 'title':
          cmp = (a.title || '').localeCompare(b.title || '');
          break;
        case 'type':
          cmp = (a.file_name?.split('.').pop() || '').localeCompare(b.file_name?.split('.').pop() || '');
          break;
        case 'size':
          cmp = (a.file_size || 0) - (b.file_size || 0);
          break;
        case 'date':
          cmp = new Date(a.created_at || 0) - new Date(b.created_at || 0);
          break;
        default:
          cmp = 0;
      }
      return tableSortDirection === 'asc' ? cmp : -cmp;
    });
  }, [tableSortColumn, tableSortDirection]);

  const handleTableSort = (column) => {
    if (tableSortColumn === column) {
      setTableSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setTableSortColumn(column);
      setTableSortDirection('asc');
    }
  };

  const sortIndicator = (col) => {
    if (tableSortColumn !== col) return null;
    return tableSortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  // Group items by time period for timeline view
  const groupItemsByTimePeriod = useCallback((items) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const thisWeekStart = new Date(today);
    thisWeekStart.setDate(today.getDate() - today.getDay() + 1); // Monday
    const lastWeekStart = new Date(thisWeekStart);
    lastWeekStart.setDate(thisWeekStart.getDate() - 7);
    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);

    const groups = {};
    const sorted = [...items].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));

    sorted.forEach(item => {
      const d = item.created_at ? new Date(item.created_at) : null;
      let label;
      let order;

      if (!d) {
        label = 'Undated';
        order = 999999;
      } else if (d >= today) {
        label = 'Today';
        order = 0;
      } else if (d >= yesterday) {
        label = 'Yesterday';
        order = 1;
      } else if (d >= thisWeekStart) {
        label = 'This Week';
        order = 2;
      } else if (d >= lastWeekStart) {
        label = 'Last Week';
        order = 3;
      } else if (d >= thisMonthStart) {
        label = 'This Month';
        order = 4;
      } else if (d >= lastMonthStart) {
        label = 'Last Month';
        order = 5;
      } else {
        label = d.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
        order = 10000 - (d.getFullYear() * 12 + d.getMonth());
      }

      if (!groups[label]) groups[label] = { label, order, items: [] };
      groups[label].items.push(item);
    });

    return Object.values(groups).sort((a, b) => a.order - b.order);
  }, []);

  const handleFolderDownload = async (e, folder) => {
    e.stopPropagation();
    setDownloadingFolder(folder.id);
    try {
      const response = await resourcesAPI.downloadFolder(folder.id);
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${folder.name}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      console.error('Folder download error:', err);
    } finally {
      setDownloadingFolder(null);
    }
  };

  if (isLoading) {
    return (
      <div className="resources-app">
        <div className="resources-loading">
          <div className="resources-loading-spinner" />
          <p>Loading resources...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="resources-app">
      {/* Sidebar */}
      <aside className="resources-sidebar">
        <div className="resources-sidebar-header">
          <h3>Resources</h3>
          <p>Documents & Files</p>
        </div>

        <nav className="resources-categories">
          {categories.map((category) => (
            <button
              key={category.id}
              className={`resources-category-btn ${selectedCategory?.id === category.id ? 'active' : ''}`}
              onClick={() => handleCategoryClick(category)}
            >
              <span className="resources-category-icon">
                {getCategoryIcon(category.icon)}
              </span>
              <span className="resources-category-info">
                <span className="resources-category-name">{category.name}</span>
                <span className="resources-category-count">{category.item_count} items</span>
              </span>
            </button>
          ))}
        </nav>
      </aside>

      {/* Main Content */}
      <main className="resources-main">
        {selectedCategory && (
          <>
            {/* Header */}
            <header className="resources-header">
              <div className="resources-header-title">
                <div className="resources-header-icon">
                  {getCategoryIcon(selectedCategory.icon)}
                </div>
                <div>
                  <h2>{selectedCategory.name}</h2>
                  <p>{selectedCategory.item_count} items available</p>
                </div>
              </div>

              <div className="resources-header-actions">
                {/* Search */}
                <div className="resources-search">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search all resources..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>

                {/* View Toggle */}
                <div className="resources-view-toggle">
                  <button
                    className={viewMode === 'grid' ? 'active' : ''}
                    onClick={() => setViewMode('grid')}
                    title="Grid view"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="7" height="7" />
                      <rect x="14" y="3" width="7" height="7" />
                      <rect x="3" y="14" width="7" height="7" />
                      <rect x="14" y="14" width="7" height="7" />
                    </svg>
                  </button>
                  <button
                    className={viewMode === 'list' ? 'active' : ''}
                    onClick={() => setViewMode('list')}
                    title="List view"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="8" y1="6" x2="21" y2="6" />
                      <line x1="8" y1="12" x2="21" y2="12" />
                      <line x1="8" y1="18" x2="21" y2="18" />
                      <line x1="3" y1="6" x2="3.01" y2="6" />
                      <line x1="3" y1="12" x2="3.01" y2="12" />
                      <line x1="3" y1="18" x2="3.01" y2="18" />
                    </svg>
                  </button>
                  <button
                    className={viewMode === 'compact' ? 'active' : ''}
                    onClick={() => setViewMode('compact')}
                    title="Compact view"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="3" y1="5" x2="21" y2="5" />
                      <line x1="3" y1="9" x2="21" y2="9" />
                      <line x1="3" y1="13" x2="21" y2="13" />
                      <line x1="3" y1="17" x2="21" y2="17" />
                      <line x1="3" y1="21" x2="21" y2="21" />
                    </svg>
                  </button>
                  <button
                    className={viewMode === 'table' ? 'active' : ''}
                    onClick={() => setViewMode('table')}
                    title="Table view"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                      <line x1="3" y1="9" x2="21" y2="9" />
                      <line x1="3" y1="15" x2="21" y2="15" />
                      <line x1="9" y1="3" x2="9" y2="21" />
                    </svg>
                  </button>
                  <button
                    className={viewMode === 'timeline' ? 'active' : ''}
                    onClick={() => setViewMode('timeline')}
                    title="Timeline view"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="6" y1="3" x2="6" y2="21" />
                      <circle cx="6" cy="6" r="2" fill="currentColor" />
                      <circle cx="6" cy="13" r="2" fill="currentColor" />
                      <circle cx="6" cy="20" r="2" fill="currentColor" />
                      <line x1="10" y1="6" x2="21" y2="6" />
                      <line x1="10" y1="13" x2="21" y2="13" />
                      <line x1="10" y1="20" x2="18" y2="20" />
                    </svg>
                  </button>
                </div>
              </div>
            </header>

            {/* Content Area */}
            <div className="resources-content">
              {loadingItems ? (
                <div className="resources-loading">
                  <div className="resources-loading-spinner" />
                  <p>Loading files...</p>
                </div>
              ) : searchQuery && searchQuery.trim().length >= 2 ? (
                // Global Search Results
                <div className="resources-section">
                  <div className="resources-section-header">
                    <h4>Search Results</h4>
                    {!searchLoading && searchResults && (
                      <span className="resources-section-count">{searchResults.length} found across all categories</span>
                    )}
                  </div>
                  {searchLoading ? (
                    <div className="resources-loading">
                      <div className="resources-loading-spinner" />
                      <p>Searching...</p>
                    </div>
                  ) : searchResults && searchResults.length === 0 ? (
                    <div className="resources-empty">
                      <p>No files match your search.</p>
                    </div>
                  ) : searchResults ? (
                    viewMode === 'table' ? (
                      <TableView
                        items={getSortedItems(searchResults)}
                        onDownload={handleDownload}
                        onPreview={handlePreview}
                        onActualDownload={handleActualDownload}
                        formatFileSize={formatFileSize}
                        formatDate={formatDate}
                        onSort={handleTableSort}
                        sortIndicator={sortIndicator}
                        highlightedItemId={highlightedItemId}
                        showLocation
                      />
                    ) : viewMode === 'timeline' ? (
                      <TimelineView
                        groups={groupItemsByTimePeriod(searchResults)}
                        onDownload={handleDownload}
                        onPreview={handlePreview}
                        onActualDownload={handleActualDownload}
                        formatFileSize={formatFileSize}
                        formatDate={formatDate}
                        highlightedItemId={highlightedItemId}
                      />
                    ) : (
                      <div className={`resources-items resources-items-${viewMode}`}>
                        {searchResults.map((item) => (
                          <FileCard
                            key={item.id}
                            item={item}
                            viewMode={viewMode}
                            onDownload={handleDownload}
                            onPreview={handlePreview}
                            onActualDownload={handleActualDownload}
                            formatFileSize={formatFileSize}
                            formatDate={formatDate}
                            highlighted={highlightedItemId === item.id}
                            locationLabel={[item.category_name, item.folder_name].filter(Boolean).join(' / ')}
                          />
                        ))}
                      </div>
                    )
                  ) : null}
                </div>
              ) : (
                // Normal View with Folders
                <>
                  {/* Recursive folder renderer */}
                  {(() => {
                    const renderFolder = (folder, depth = 0) => {
                      const indent = depth * 1.5; // rem units for indentation
                      const hasItems = folder.items?.length > 0;
                      const hasSubfolders = folder.subfolders?.length > 0;

                      // Count total items including subfolders recursively
                      const countTotalItems = (f) => {
                        let count = f.item_count || 0;
                        if (f.subfolders) {
                          for (const sf of f.subfolders) {
                            count += countTotalItems(sf);
                          }
                        }
                        return count;
                      };
                      const totalItems = countTotalItems(folder);

                      return (
                        <div key={folder.id} className="resources-section" style={{ marginLeft: `${indent}rem` }}>
                          <div
                            className={`resources-folder-header ${depth > 0 ? 'resources-subfolder-header' : ''}`}
                            onClick={() => toggleFolder(folder.id)}
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="resources-folder-info">
                              <span className={`resources-folder-icon ${expandedFolders[folder.id] ? 'open' : ''}`}>
                                <FileIcon type={expandedFolders[folder.id] ? 'folderOpen' : 'folder'} size={depth > 0 ? 18 : 20} />
                              </span>
                              <span className="resources-folder-name">{folder.name}</span>
                              <span className="resources-folder-count">
                                {totalItems} item{totalItems !== 1 ? 's' : ''}
                                {hasSubfolders && ` · ${folder.subfolders.length} subfolder${folder.subfolders.length !== 1 ? 's' : ''}`}
                              </span>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              {totalItems > 0 && (
                                <button
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={(e) => handleFolderDownload(e, folder)}
                                  disabled={downloadingFolder === folder.id}
                                  title="Download all files as ZIP"
                                  style={{ fontSize: '0.75rem', padding: '0.2rem 0.6rem' }}
                                >
                                  {downloadingFolder === folder.id ? (
                                    <><span className="spinner-border spinner-border-sm me-1" role="status" style={{ width: '0.7rem', height: '0.7rem' }}></span>Downloading...</>
                                  ) : (
                                    <>
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="me-1" style={{ verticalAlign: 'text-bottom' }}>
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                        <polyline points="7 10 12 15 17 10" />
                                        <line x1="12" y1="15" x2="12" y2="3" />
                                      </svg>
                                      Download All
                                    </>
                                  )}
                                </button>
                              )}
                              <svg
                                className={`resources-folder-chevron ${expandedFolders[folder.id] ? 'open' : ''}`}
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <polyline points="6 9 12 15 18 9" />
                              </svg>
                            </div>
                          </div>

                          {expandedFolders[folder.id] && (
                            <>
                              {/* Render folder items first */}
                              {hasItems && (
                                <div style={{ marginLeft: `${indent + 1}rem` }}>
                                  {viewMode === 'table' ? (
                                    <TableView
                                      items={getSortedItems(folder.items)}
                                      onDownload={handleDownload}
                                      onPreview={handlePreview}
                                      onActualDownload={handleActualDownload}
                                      formatFileSize={formatFileSize}
                                      formatDate={formatDate}
                                      onSort={handleTableSort}
                                      sortIndicator={sortIndicator}
                                      highlightedItemId={highlightedItemId}
                                    />
                                  ) : viewMode === 'timeline' ? (
                                    <TimelineView
                                      groups={groupItemsByTimePeriod(folder.items)}
                                      onDownload={handleDownload}
                                      onPreview={handlePreview}
                                      onActualDownload={handleActualDownload}
                                      formatFileSize={formatFileSize}
                                      formatDate={formatDate}
                                      highlightedItemId={highlightedItemId}
                                    />
                                  ) : (
                                    <div className={`resources-items resources-items-${viewMode}`}>
                                      {folder.items.map((item) => (
                                        <FileCard
                                          key={item.id}
                                          item={item}
                                          viewMode={viewMode}
                                          onDownload={handleDownload}
                                          onPreview={handlePreview}
                                          onActualDownload={handleActualDownload}
                                          formatFileSize={formatFileSize}
                                          formatDate={formatDate}
                                        />
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Then render subfolders recursively */}
                              {hasSubfolders && folder.subfolders.map((subfolder) => renderFolder(subfolder, depth + 1))}
                            </>
                          )}
                        </div>
                      );
                    };

                    return categoryData.folders?.map((folder) => renderFolder(folder, 0));
                  })()}

                  {/* Root Items */}
                  {categoryData.root_items?.length > 0 && (
                    <div className="resources-section">
                      {categoryData.folders?.length > 0 && (
                        <div className="resources-section-header">
                          <h4>Other Files</h4>
                        </div>
                      )}
                      {viewMode === 'table' ? (
                        <TableView
                          items={getSortedItems(categoryData.root_items)}
                          onDownload={handleDownload}
                          onPreview={handlePreview}
                          onActualDownload={handleActualDownload}
                          formatFileSize={formatFileSize}
                          formatDate={formatDate}
                          onSort={handleTableSort}
                          sortIndicator={sortIndicator}
                          highlightedItemId={highlightedItemId}
                        />
                      ) : viewMode === 'timeline' ? (
                        <TimelineView
                          groups={groupItemsByTimePeriod(categoryData.root_items)}
                          onDownload={handleDownload}
                          onPreview={handlePreview}
                          onActualDownload={handleActualDownload}
                          formatFileSize={formatFileSize}
                          formatDate={formatDate}
                          highlightedItemId={highlightedItemId}
                        />
                      ) : (
                        <div className={`resources-items resources-items-${viewMode}`}>
                          {categoryData.root_items.map((item) => (
                            <FileCard
                              key={item.id}
                              item={item}
                              viewMode={viewMode}
                              onDownload={handleDownload}
                              onPreview={handlePreview}
                              onActualDownload={handleActualDownload}
                              formatFileSize={formatFileSize}
                              formatDate={formatDate}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Empty State */}
                  {categoryData.folders?.length === 0 && categoryData.root_items?.length === 0 && (
                    <div className="resources-empty">
                      <div className="resources-empty-icon">
                        <FileIcon type="folder" size={48} />
                      </div>
                      <h4>No files yet</h4>
                      <p>This category doesn't have any files.</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </main>

      {/* PDF Preview Modals */}
      {openPdfModals.map(m => (
        <PDFPreviewModal
          key={m.id}
          isOpen={true}
          onClose={() => setOpenPdfModals(prev => prev.filter(p => p.id !== m.id))}
          pdfUrl={m.url}
          title={m.title}
          token={localStorage.getItem('access_token')}
        />
      ))}

      {/* Video Player Modals */}
      {openVideoModals.map(m => (
        <NativeVideoModal
          key={m.id}
          url={m.url}
          title={m.title}
          onClose={() => setOpenVideoModals(prev => prev.filter(v => v.id !== m.id))}
        />
      ))}
    </div>
  );
};

// File Card Component
const FileCard = ({ item, viewMode, onDownload, onPreview, onActualDownload, formatFileSize, formatDate, highlighted, locationLabel }) => {
  const fileType = getFileTypeInfo(item);
  const isVisual = ['image', 'video'].includes(fileType.icon);
  const ext = item.file_name?.split('.').pop()?.toLowerCase();
  const isPDF = ext === 'pdf';
  const isVideo = ['mp4', 'webm', 'mov', 'avi', 'mkv'].includes(ext);
  const hasThumbnail = item.preview_url && item.preview_url.startsWith('http');
  const showBothButtons = isPDF || isVideo;

  return (
    <div
      className={`file-card file-card-${viewMode}${highlighted ? ' file-card-highlighted' : ''}`}
      data-resource-id={item.id}
      onClick={showBothButtons ? undefined : () => onDownload(item)}
    >
      {/* Icon/Thumbnail Area */}
      <div
        className="file-card-icon"
        style={{ backgroundColor: hasThumbnail ? '#f5f5f5' : fileType.bgColor }}
      >
        {hasThumbnail ? (
          <img
            src={item.preview_url}
            alt={item.title}
            className="file-card-thumbnail"
            onError={(e) => {
              // Fallback to icon if thumbnail fails to load
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <span
          style={{
            color: fileType.color,
            display: hasThumbnail ? 'none' : 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          className="file-card-icon-fallback"
        >
          <FileIcon type={fileType.icon} size={viewMode === 'grid' ? 32 : viewMode === 'compact' ? 16 : 24} />
        </span>
        {isVisual && (
          <div className="file-card-preview-badge">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
              <polygon points="5 3 19 12 5 21 5 3" />
            </svg>
          </div>
        )}
      </div>

      {/* File Info */}
      <div className="file-card-info">
        <h5 className="file-card-title">{item.title}</h5>
        {item.description && viewMode === 'grid' && (
          <p className="file-card-description">{item.description}</p>
        )}
        <div className="file-card-meta">
          <span
            className="file-card-type"
            style={{ backgroundColor: fileType.bgColor, color: fileType.color }}
          >
            {fileType.label}
          </span>
          {item.file_size > 0 && (
            <span className="file-card-size">{formatFileSize(item.file_size)}</span>
          )}
          {item.created_at && (viewMode === 'list' || viewMode === 'compact') && (
            <span className="file-card-date">{formatDate(item.created_at)}</span>
          )}
        </div>
        {locationLabel && (
          <div className="file-card-location">{locationLabel}</div>
        )}
      </div>

      {/* Action */}
      {showBothButtons ? (
        <div className="file-card-actions-group" style={{ display: 'flex', gap: '0.5rem', padding: '0.75rem 1rem' }}>
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={(e) => {
              e.stopPropagation();
              onPreview(item);
            }}
            style={{ flex: 1 }}
          >
            <i className="bi bi-eye me-1"></i>
            Preview
          </button>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={(e) => {
              e.stopPropagation();
              onActualDownload(item);
            }}
            style={{ flex: 1 }}
          >
            <i className="bi bi-download me-1"></i>
            Download
          </button>
        </div>
      ) : (
        <div className="file-card-action">
          <span className="file-card-action-text">
            {item.item_type === 'link' ? 'Open' : 'Download'}
          </span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {item.item_type === 'link' ? (
              <>
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </>
            ) : (
              <>
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </>
            )}
          </svg>
        </div>
      )}
    </div>
  );
};

// Table View component
const TableView = ({ items, onDownload, onPreview, onActualDownload, formatFileSize, formatDate, onSort, sortIndicator, highlightedItemId, showLocation }) => {
  return (
    <div className="resources-table-wrapper">
      <table className="resources-table">
        <thead>
          <tr>
            <th onClick={() => onSort('title')} style={{ cursor: 'pointer' }}>
              Title{sortIndicator('title')}
            </th>
            <th onClick={() => onSort('type')} style={{ cursor: 'pointer', width: '80px' }}>
              Type{sortIndicator('type')}
            </th>
            <th onClick={() => onSort('size')} style={{ cursor: 'pointer', width: '90px' }}>
              Size{sortIndicator('size')}
            </th>
            <th onClick={() => onSort('date')} style={{ cursor: 'pointer', width: '120px' }}>
              Date{sortIndicator('date')}
            </th>
            {showLocation && <th style={{ width: '160px' }}>Location</th>}
          </tr>
        </thead>
        <tbody>
          {items.map(item => {
            const fileType = getFileTypeInfo(item);
            const ext = item.file_name?.split('.').pop()?.toLowerCase();
            const isPDF = ext === 'pdf';
            const isVideo = ['mp4', 'webm', 'mov', 'avi', 'mkv'].includes(ext);
            const showBothButtons = isPDF || isVideo;

            return (
              <tr
                key={item.id}
                className={highlightedItemId === item.id ? 'resources-table-row-highlighted' : ''}
                onClick={() => onDownload(item)}
                style={{ cursor: 'pointer' }}
              >
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ color: fileType.color, flexShrink: 0 }}>
                      <FileIcon type={fileType.icon} size={16} />
                    </span>
                    <span className="resources-table-title">{item.title}</span>
                  </div>
                </td>
                <td>
                  <span
                    className="file-card-type"
                    style={{ backgroundColor: fileType.bgColor, color: fileType.color, fontSize: '0.65rem' }}
                  >
                    {fileType.label}
                  </span>
                </td>
                <td><small className="text-muted">{formatFileSize(item.file_size)}</small></td>
                <td><small className="text-muted">{formatDate(item.created_at)}</small></td>
                {showLocation && (
                  <td><small className="text-muted">{[item.category_name, item.folder_name].filter(Boolean).join(' / ')}</small></td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// Timeline View component
const TimelineView = ({ groups, onDownload, onPreview, onActualDownload, formatFileSize, formatDate, highlightedItemId }) => {
  return (
    <div className="resources-timeline">
      {groups.map(group => (
        <div key={group.label} className="resources-timeline-group">
          <div className="resources-timeline-label">
            <div className="resources-timeline-dot" />
            <h4>{group.label}</h4>
            <span className="resources-section-count">{group.items.length} item{group.items.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="resources-timeline-items">
            {group.items.map(item => (
              <FileCard
                key={item.id}
                item={item}
                viewMode="list"
                onDownload={onDownload}
                onPreview={onPreview}
                onActualDownload={onActualDownload}
                formatFileSize={formatFileSize}
                formatDate={formatDate}
                highlighted={highlightedItemId === item.id}
                locationLabel={item._folderName || undefined}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// Native video player modal for uploaded video files
const NativeVideoModal = ({ url, title, onClose }) => {
  const [videoSrc, setVideoSrc] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    const fetchVideoUrl = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const apiUrl = `${url}${url.includes('?') ? '&' : '?'}direct_url=true`;
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) throw new Error('Failed to get video URL');
        const data = await response.json();
        if (!data.url) throw new Error('No URL in response');

        setVideoSrc(data.url);
        setLoading(false);
      } catch (err) {
        console.error('Video load error:', err);
        setError('Failed to load video');
        setLoading(false);
      }
    };
    fetchVideoUrl();
  }, [url]);

  return (
    <ModalShell
      isOpen={true}
      onClose={onClose}
      title={title || 'Video'}
      maxWidth="1200px"
      initialHeight="auto"
      bodyStyle={{ background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '300px' }}
      headerStyle={{ background: '#1a1a1a', borderBottom: '1px solid #333' }}
      titleStyle={{ color: '#fff' }}
    >
      {loading && (
        <div style={{ textAlign: 'center', color: 'white', padding: '40px' }}>
          <div className="spinner-border text-light" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p style={{ marginTop: '12px' }}>Loading video...</p>
        </div>
      )}
      {error && (
        <div style={{ textAlign: 'center', color: 'white', padding: '40px' }}>
          <p>{error}</p>
        </div>
      )}
      {videoSrc && !loading && !error && (
        <video
          controls
          autoPlay
          style={{ maxWidth: '100%', maxHeight: '80vh' }}
          src={videoSrc}
        />
      )}
    </ModalShell>
  );
};

export default ResourcesApp;
